import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import ButtonIcon from './ButtonIcon';
import { IconSend, IconSpinner } from './icons/ActionIcons';
import { useLanguage } from '../LanguageContext';
import { splitPaymentAmount } from '../utils/contributionSplit';
import { formatUsd } from '../utils/formatMoney';

function projectLabel(project, t) {
  if (!project) return '';
  if (project.sectorId) {
    const sectorKey = `sectors.${project.sectorId}.title`;
    const sectorLabel = t(sectorKey);
    if (sectorLabel !== sectorKey) return sectorLabel;

    const resourceKey = `resources.categories.${project.sectorId}`;
    const resourceLabel = t(resourceKey);
    if (resourceLabel !== resourceKey) return resourceLabel;
  }
  return project.title;
}

export default function ContributionFormSection({
  donationProjects = [],
  investmentProjects = [],
  paymentMethods = [],
  optionsLoading = false,
  optionsError = '',
  onRetryOptions,
}) {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    name: '',
    whatsappPhone: '',
    totalAmount: '',
    donationProjectId: '',
    investmentProjectId: '',
    paymentMethodId: '',
  });
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const totalAmount = form.totalAmount === '' ? 0 : Number(form.totalAmount);
  const split = useMemo(() => splitPaymentAmount(totalAmount), [totalAmount]);
  const donationOptions = Array.isArray(donationProjects) ? donationProjects : [];
  const investmentOptions = Array.isArray(investmentProjects) ? investmentProjects : [];
  const accountOptions = Array.isArray(paymentMethods) ? paymentMethods : [];
  const paymentReady =
    !optionsLoading &&
    accountOptions.length > 0 &&
    donationOptions.length > 0 &&
    investmentOptions.length > 0;

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!paymentReady) {
      setError(t('contributions.optionsLoadError'));
      return;
    }
    if (!Number.isFinite(totalAmount) || totalAmount <= 0) {
      setError(t('contributions.totalAmountRequired'));
      return;
    }
    if (!form.donationProjectId) {
      setError(t('contributions.donationProjectRequired'));
      return;
    }
    if (!form.investmentProjectId) {
      setError(t('contributions.investmentProjectRequired'));
      return;
    }
    if (!form.paymentMethodId) {
      setError(t('contributions.paymentAccountRequired'));
      return;
    }
    if (!ageConfirmed) {
      setError(t('contributions.ageRequired'));
      return;
    }
    if (!termsAccepted) {
      setError(t('contributions.termsRequired'));
      return;
    }

    setSubmitting(true);

    try {
      await api.submitContribution({
        name: form.name,
        whatsappPhone: form.whatsappPhone,
        totalAmount,
        donationProjectId: form.donationProjectId,
        investmentProjectId: form.investmentProjectId,
        paymentMethodId: form.paymentMethodId,
        ageConfirmed: true,
        termsAccepted: true,
      });
      setSuccess(true);
      setForm({
        name: '',
        whatsappPhone: '',
        totalAmount: '',
        donationProjectId: '',
        investmentProjectId: '',
        paymentMethodId: '',
      });
      setAgeConfirmed(false);
      setTermsAccepted(false);
    } catch (err) {
      setError(err.message || t('contributions.submitError'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section
      className="section section--contribution-form"
      id="contribute"
      aria-labelledby="contribute-heading"
    >
      <div className="section__head">
        <h2 id="contribute-heading">{t('contributions.formTitle')}</h2>
        <p className="section__sub">{t('contributions.formSub')}</p>
      </div>

      <form className="contribution-form" onSubmit={handleSubmit}>
        <div className="contribution-form__grid">
          <label className="contribution-form__label">
            {t('contributions.name')}
            <input
              type="text"
              className="contribution-form__input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder={t('contributions.namePlaceholder')}
              required
              autoComplete="name"
            />
          </label>
          <label className="contribution-form__label">
            {t('contributions.whatsapp')}
            <input
              type="tel"
              className="contribution-form__input"
              value={form.whatsappPhone}
              onChange={(e) => setForm({ ...form, whatsappPhone: e.target.value })}
              placeholder={t('contributions.whatsappPlaceholder')}
              required
              autoComplete="tel"
              dir="ltr"
            />
          </label>

          <fieldset className="contribution-form__payment contribution-form__label--full">
            <legend className="contribution-form__payment-title">
              {t('contributions.paymentSectionTitle')}
            </legend>

            {optionsLoading ? (
              <p className="contribution-form__payment-status" role="status">
                {t('contributions.loadingOptions')}
              </p>
            ) : null}

            {optionsError ? (
              <div className="contribution-form__payment-error" role="alert">
                <p>{optionsError}</p>
                {onRetryOptions ? (
                  <button
                    type="button"
                    className="contribution-form__retry"
                    onClick={onRetryOptions}
                  >
                    {t('contributions.retryLoad')}
                  </button>
                ) : null}
              </div>
            ) : null}

            {!optionsLoading && paymentReady ? (
              <div className="contribution-form__payment-grid">
                <label className="contribution-form__label contribution-form__label--full">
                  {t('contributions.paymentAccount')}
                  <select
                    className="contribution-form__input contribution-form__select"
                    value={form.paymentMethodId}
                    onChange={(e) => setForm({ ...form, paymentMethodId: e.target.value })}
                    required
                    disabled={!accountOptions.length}
                  >
                    <option value="">{t('contributions.selectPaymentAccount')}</option>
                    {accountOptions.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.label} ({m.value})
                      </option>
                    ))}
                  </select>
                </label>

                <label className="contribution-form__label contribution-form__label--full">
                  {t('contributions.totalAmount')}
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    className="contribution-form__input"
                    value={form.totalAmount}
                    onChange={(e) => setForm({ ...form, totalAmount: e.target.value })}
                    placeholder="0"
                    required
                  />
                </label>

                {totalAmount > 0 ? (
                  <div className="contribution-form__split contribution-form__label--full">
                    <p className="contribution-form__split-title">{t('contributions.splitTitle')}</p>
                    <ul className="contribution-form__split-list">
                      <li>
                        <span>{t('contributions.charitableGross')} (40%)</span>
                        <strong>{formatUsd(split.donation)}</strong>
                      </li>
                      <li className="contribution-form__split-detail">
                        <span>{t('contributions.adminFee')}</span>
                        <strong>− {formatUsd(split.charitableAdminFee)}</strong>
                      </li>
                      <li>
                        <span>{t('contributions.netCharitable')}</span>
                        <strong>{formatUsd(split.netCharitable)}</strong>
                      </li>
                      <li>
                        <span>{t('contributions.capitalFund')} (60%)</span>
                        <strong>{formatUsd(split.investment)}</strong>
                      </li>
                    </ul>
                    <p className="contribution-form__split-note">{t('contributions.splitNote')}</p>
                  </div>
                ) : null}

                <label className="contribution-form__label">
                  {t('contributions.charitableProgram')}
                  <select
                    className="contribution-form__input contribution-form__select"
                    value={form.donationProjectId}
                    onChange={(e) => setForm({ ...form, donationProjectId: e.target.value })}
                    required
                    disabled={!donationOptions.length}
                  >
                    <option value="">{t('contributions.selectProject')}</option>
                    {donationOptions.map((p) => (
                      <option key={p.id} value={p.id}>
                        {projectLabel(p, t)}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="contribution-form__label">
                  {t('contributions.capitalFundSector')}
                  <select
                    className="contribution-form__input contribution-form__select"
                    value={form.investmentProjectId}
                    onChange={(e) => setForm({ ...form, investmentProjectId: e.target.value })}
                    required
                    disabled={!investmentOptions.length}
                  >
                    <option value="">{t('contributions.selectProject')}</option>
                    {investmentOptions.map((p) => (
                      <option key={p.id} value={p.id}>
                        {projectLabel(p, t)}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            ) : null}
          </fieldset>

          <div className="contribution-form__legal contribution-form__label--full">
            <label className="contribution-form__checkbox">
              <input
                type="checkbox"
                checked={ageConfirmed}
                onChange={(e) => setAgeConfirmed(e.target.checked)}
              />
              <span>{t('contributions.ageConfirm')}</span>
            </label>
            <label className="contribution-form__checkbox">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
              />
              <span>
                {t('contributions.termsConfirmPrefix')}{' '}
                <Link to="/terms" className="contribution-form__terms-link">
                  {t('contributions.termsConfirmLink')}
                </Link>
              </span>
            </label>
            <p className="contribution-form__legal-note">{t('contributions.refundNotice')}</p>
            <p className="contribution-form__legal-note">{t('contributions.riskDisclaimer')}</p>
          </div>
        </div>

        <p className="contribution-form__hint">{t('contributions.formHint')}</p>

        {error && (
          <p className="contribution-form__error" role="alert">
            {error}
          </p>
        )}
        {success && (
          <p className="contribution-form__success" role="status">
            {t('contributions.submitSuccess')}
          </p>
        )}

        <button
          type="submit"
          className="contribution-form__submit btn-with-icon"
          disabled={submitting || optionsLoading || !paymentReady}
        >
          <ButtonIcon icon={submitting ? IconSpinner : IconSend} size={18}>
            {submitting ? t('contributions.submitting') : t('contributions.submit')}
          </ButtonIcon>
        </button>
      </form>
    </section>
  );
}
